import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Baby } from '@/types';
import { useChat } from '@/hooks/useChat';
import { useChatStarters } from '@/hooks/useChatStarters';
import { useAuthStore } from '@/stores/useAuthStore';
import { MessageBubble } from './MessageBubble';
import { InputBar } from './InputBar';
import { TriageBanner } from './TriageBanner';
import { PromptChip } from './PromptChip';
import { BabyAgeChip } from '@/components/profile/BabyAgeChip';
import { SignupWall } from '@/components/auth/SignupWall';
import { BackLink } from '@/components/ui/PageState';
import { Icon, IconTile } from '@/components/ui/icons';
import { getFollowUps } from '@/lib/follow-ups';
import { isGerman } from '@/i18n/locale';

interface Props {
  baby: Baby;
  initialSessionId?: string | null;
  initialMessages?: Parameters<typeof useChat>[0]['initialMessages'];
}

// Threshold (px) — if the user is within this many pixels of the bottom,
// they're considered "at the bottom" and we keep auto-scrolling. If
// they're further up, they're reading and we leave them alone.
//
// 80px is roughly two lines of body text + the bottom padding of the
// scroll container. Big enough that small mobile rubber-banding doesn't
// disengage stick-to-bottom; small enough that scrolling up to read
// cleanly stops the yank.
const STICK_TO_BOTTOM_THRESHOLD_PX = 80;

export function ChatWindow({ baby, initialSessionId, initialMessages }: Props) {
  const { messages, streamingText, isStreaming, error, lastTriage, send } = useChat({
    babyId: baby.id,
    initialSessionId,
    initialMessages,
  });

  // We branch the rate-limit render on whether the user is signed in
  // anonymously. Real free users see the upgrade-to-Pro pitch.
  // Anonymous users see the soft signup wall instead.
  const authUser = useAuthStore((s) => s.user);
  const isAnonymous = Boolean(authUser?.is_anonymous);

  // Inline i18n for chat surfaces a German anon mum sees in her first
  // few minutes. AI response text itself comes from Claude and mirrors
  // the user's language automatically; only the chrome around it is
  // localized here. Memoized so it's stable across renders.
  const t = useMemo(() => {
    if (isGerman()) {
      return {
        chatHeader: 'Chat',
        backAria: 'Zurück zu „Diese Woche"',
        historyAria: 'Frühere Gespräche',
        thinking: 'Alma denkt nach…',
        inputPlaceholder: (n: string) => `Frag alles über ${n}`,
        emptyTitle: (n: string) => `Hi — was beschäftigt dich gerade bei ${n}?`,
        keepGoing: 'Weiter so',
        rateLimitTitle: (limit: number) =>
          `Du hast deine ${limit} kostenlosen Fragen für diesen Monat aufgebraucht`,
        rateLimitBody:
          'Hol dir Pro für unbegrenzte Fragen, bis zu 3 Baby-Profile und 90 Tage Gesprächsverlauf. €9,99/Monat oder €79,99/Jahr.',
        rateLimitCta: 'Pro holen →',
        rateLimitReset: 'Kostenlose Fragen werden am 1. des Monats zurückgesetzt.',
        scrollToLatest: 'Zur letzten Nachricht',
      };
    }
    return {
      chatHeader: 'Chat',
      backAria: 'Back to This Week',
      historyAria: 'Past conversations',
      thinking: 'Alma is thinking…',
      inputPlaceholder: (n: string) => `Ask anything about ${n}`,
      emptyTitle: (n: string) => `Hi — what's on your mind about ${n}?`,
      keepGoing: 'Keep going',
      rateLimitTitle: (limit: number) =>
        `You've used all ${limit} free questions this month`,
      rateLimitBody:
        'Upgrade to Pro for unlimited questions, up to 3 babies, and 90-day conversation history. $9.99/month or $79.99/year.',
      rateLimitCta: 'Get Pro →',
      rateLimitReset: 'Free questions reset on the 1st of next month.',
      scrollToLatest: 'Scroll to latest message',
    };
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  // True iff the user is currently anchored at the bottom of the scroll
  // container. Default true (fresh chat starts at bottom). Updated on scroll.
  const stickToBottomRef = useRef<boolean>(true);

  const [params, setParams] = useSearchParams();
  const autoSentRef = useRef(false);

  // Auto-send a prompt arriving via ?prompt= (from journey "Ask Alma" buttons)
  useEffect(() => {
    if (autoSentRef.current) return;
    const prompt = params.get('prompt');
    if (prompt && !isStreaming && messages.length === 0 && !streamingText) {
      autoSentRef.current = true;
      void send({ message: prompt });
      const next = new URLSearchParams(params);
      next.delete('prompt');
      setParams(next, { replace: true });
    }
  }, [params, send, isStreaming, messages.length, streamingText, setParams]);

  // ── Stick-to-bottom scroll behavior ──────────────────────────────
  // Track whether the user is at the bottom; when streaming, only auto-
  // scroll if they're already at the bottom. This prevents the screen
  // from yanking out from under a parent who's reading the start of an
  // answer while later tokens stream in.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // When new content arrives, auto-scroll ONLY if the user is anchored.
  // We use 'auto' (instant) instead of 'smooth' for streaming because
  // smooth scroll on every token causes a jittery animation feel.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!stickToBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }, [messages.length, streamingText]);

  // When the user sends a NEW message, force-anchor to the bottom so
  // they see their own message land. This re-engages stick-to-bottom
  // even if they had scrolled up before sending.
  const previousUserMessageCount = useRef(0);
  useEffect(() => {
    const userMessageCount = messages.filter((m) => m.role === 'user').length;
    if (userMessageCount > previousUserMessageCount.current) {
      stickToBottomRef.current = true;
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    previousUserMessageCount.current = userMessageCount;
  }, [messages]);

  // Keep a "scroll back to bottom" button visible when the user has
  // scrolled up while streaming — gives them an explicit way to re-anchor.
  const [showScrollButton, setShowScrollButton] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollButton(distanceFromBottom > STICK_TO_BOTTOM_THRESHOLD_PX);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const isEmpty = messages.length === 0 && !streamingText;

  // Tighter placeholder — drops the "is X old" tail per tester feedback.
  // The age context is still injected into the model via the system prompt;
  // the user just doesn't see it surfaced as UI noise.
  const inputPlaceholder = isStreaming ? t.thinking : t.inputPlaceholder(baby.name);

  const lastMsg = messages[messages.length - 1];
  const showFollowUps =
    !isStreaming &&
    !streamingText &&
    !error &&
    lastMsg?.role === 'assistant' &&
    lastMsg?.intent != null;

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-cream">
      <header className="shrink-0 border-b border-primary-50 bg-cream/95 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-chat px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <BackLink to="/today" label={t.backAria} />
            <h1 className="font-display text-lg font-semibold text-slate">{t.chatHeader}</h1>
          </div>
          <div className="flex items-center gap-1">
            {/* History link — opens the past-conversations page */}
            <Link to="/chat/history" className="icon-btn" aria-label={t.historyAria} title={t.historyAria}>
              <Icon.Clock size={20} />
            </Link>
            <BabyAgeChip baby={baby} />
          </div>
        </div>
      </header>

      {lastTriage && <TriageBanner level={lastTriage} />}

      <main ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto relative">
        <div className="mx-auto max-w-chat px-4 py-6 space-y-4">
          {isEmpty && (
            <EmptyState
              babyName={baby.name}
              babyId={baby.id}
              onSuggest={(text) => send({ message: text })}
            />
          )}

          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}

          {streamingText && (
            <MessageBubble
              streaming
              message={{
                id: 'streaming',
                session_id: '',
                user_id: '',
                role: 'assistant',
                content: streamingText,
                content_type: 'text',
                image_url: null,
                token_count: null,
                rag_sources: null,
                intent: null,
                triage_level: null,
                created_at: new Date().toISOString(),
              }}
            />
          )}

          {showFollowUps && (
            <FollowUpRow
              babyName={baby.name}
              intent={lastMsg?.intent ?? null}
              onTap={(text) => send({ message: text })}
            />
          )}

          {error && error.kind === 'generic' && (
            <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger-700">
              {error.message}
            </div>
          )}

          {/*
            Rate-limit render branches on auth state:
              - Anonymous user → SignupWall (conversion moment).
                After successful conversion, the wall calls onConverted
                which clears the error from useChat state and lets the
                mum send her next question right away.
              - Real free user → existing upgrade-to-Pro card (unchanged).
          */}
          {error && error.kind === 'rate_limit' && isAnonymous && (
            <SignupWall
              babyName={baby.name}
              onConverted={() => {
                // Force a refresh of the chat state so the rate_limit
                // error clears. The simplest correct way: reload the
                // route. The session_id and message history persist via
                // useChat's load from the DB, so nothing is lost.
                window.location.reload();
              }}
            />
          )}

          {error && error.kind === 'rate_limit' && !isAnonymous && (
            <div className="rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/15 to-accent/5 p-5">
              <div className="flex items-start gap-3">
                <IconTile tone="accent">
                  <Icon.Sparkles size={20} />
                </IconTile>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg font-semibold text-slate leading-snug">
                    {t.rateLimitTitle(error.limit)}
                  </h3>
                  <p className="text-sm text-slate-muted mt-1.5 leading-relaxed">
                    {t.rateLimitBody}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                      to="/pricing"
                      className="btn-primary text-sm py-2 px-4 inline-flex items-center"
                    >
                      {t.rateLimitCta}
                    </Link>
                    <span className="text-xs text-slate-muted self-center ml-1">
                      {t.rateLimitReset}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating "scroll to latest" button — appears when the user has
            scrolled up while content is being added. Tap to re-anchor. */}
        {showScrollButton && (
          <button
            type="button"
            onClick={scrollToBottom}
            aria-label={t.scrollToLatest}
            title={t.scrollToLatest}
            className="sticky bottom-4 ml-auto mr-4 mb-4 w-10 h-10 rounded-full bg-slate text-white shadow-lg hover:bg-slate/90 active:scale-95 flex items-center justify-center transition-[background-color,transform]"
            style={{ float: 'right' }}
          >
            <Icon.ArrowDown size={18} strokeWidth={2.5} />
          </button>
        )}
      </main>

      <InputBar
        onSend={(text) => send({ message: text })}
        disabled={isStreaming}
        placeholder={inputPlaceholder}
      />
    </div>
  );
}

function EmptyState({
  babyName,
  babyId,
  onSuggest,
}: {
  babyName: string;
  babyId: string;
  onSuggest: (text: string) => void;
}) {
  const { starters, loading } = useChatStarters(babyId);
  const title = isGerman()
    ? `Hi — was beschäftigt dich gerade bei ${babyName}?`
    : `Hi — what's on your mind about ${babyName}?`;

  return (
    <div className="text-center py-8 sm:py-10">
      <div className="font-display text-2xl text-slate font-semibold">
        {title}
      </div>
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md mx-auto">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-primary-100 bg-white px-4 py-3 animate-pulse"
              >
                <div className="h-3 w-16 bg-primary-100 rounded" />
                <div className="h-3 w-3/4 bg-primary-50 rounded mt-2" />
              </div>
            ))
          : starters.map((s, i) => (
              <PromptChip
                key={`${s.label}-${i}`}
                variant="block"
                emoji={s.emoji}
                label={s.label}
                description={s.text}
                onClick={() => onSuggest(s.text)}
              />
            ))}
      </div>
    </div>
  );
}

function FollowUpRow({
  babyName,
  intent,
  onTap,
}: {
  babyName: string;
  intent: string | null;
  onTap: (text: string) => void;
}) {
  const followUps = getFollowUps(intent, babyName);
  const label = isGerman() ? 'Weiter so' : 'Keep going';

  return (
    <div className="pt-1 -mt-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-muted mb-2 ml-1">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {followUps.map((f, i) => (
          <PromptChip
            key={`${i}-${f.text}`}
            label={f.text}
            emoji={f.emoji}
            onClick={() => onTap(f.text)}
          />
        ))}
      </div>
    </div>
  );
}
